import React from 'react';
import { clsx } from 'clsx';

type FetchPriority = "high" | "low" | "auto";

type SeoImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  fetchPriority?: FetchPriority;
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  loading?: 'lazy' | 'eager';
  priority?: boolean; // For LCP optimization
  sizes?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
};

export function SeoImage({ fetchPriority, ...imgProps }: SeoImageProps) {
  const {
    src,
    alt,
    width,
    height,
    className,
    loading = 'lazy',
    priority = false,
    sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
    style,
    onLoad,
  } = imgProps;
  if (!fetchPriority && priority) {
    fetchPriority = 'high';
  }
  // Generate srcset for responsive images
  const generateSrcSet = (baseSrc: string) => {
    const encodedSrc = encodeURI(baseSrc);
    const widths = [320, 640, 960, 1280, 1920];
    return widths
      .filter(w => w <= width)
      .map(w => `${encodedSrc}?w=${w} ${w}w`)
      .join(', ');
  };

  const encodedSrc = encodeURI(src);

  return (
    <picture>
      {/* AVIF format - highest compression */}
      <source
        srcSet={`${encodedSrc}?format=avif&width=${width}`}
        type="image/avif"
        sizes={sizes}
      />
      {/* WebP format - high compression */}
      <source
        srcSet={`${encodedSrc}?format=webp&width=${width}`}
        type="image/webp"
        sizes={sizes}
      />
      {/* Fallback to original with srcset */}
      <img
        src={`${encodedSrc}?width=${Math.min(width, 1920)}`}
        srcSet={generateSrcSet(src)}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : loading}
        decoding="async"
        {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
        className={clsx(
          'block max-w-full h-auto object-cover',
          className
        )}
        style={{ aspectRatio: `${width}/${height}`, ...style }}
        onLoad={onLoad}
      />
    </picture>
  );
}
